import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import theme from '../styles/theme';
import { validatePaymentForm } from '../services/stripeService';
import STRIPE_CONFIG from '../config/stripe';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { updateUserProgress } from '../services/funnelService';
import { createOrUpdateContact, updateContactCustomFields, addContactTag } from '../services/activeCampaignService';
import { useStripeContext } from '../contexts/StripeContext';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  max-width: 500px;
  margin: 0 auto;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    /* Ensure form has enough space to be scrollable with 20px buffer below CTA button */
    margin-bottom: 40px;
    padding-bottom: 0;
  }
`;

const Input = styled.input`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
  
  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const StripeCardElement = styled.div`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus-within {
    border-color: ${theme.colors.primary};
  }
  
  .StripeElement {
    font-family: ${theme.typography.bodyFont};
    font-size: ${theme.typography.sizes.body};
  }
  
  /* Mobile keyboard handling */
  @media (max-width: ${theme.breakpoints.tablet}) {
    /* Ensure the element doesn't cause viewport issues */
    position: relative;
    z-index: 1;
    
    /* Prevent the element from being pushed off screen */
    margin-bottom: 20px;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin-top: ${theme.spacing.lg};
  width: 100%;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    /* Ensure button is easily tappable on mobile */
    min-height: 56px;
    font-size: 1.125rem;
    /* Add extra margin to ensure it's not cut off */
    margin-bottom: 50px;
    /* Ensure button stays in viewport */
    position: relative;
    z-index: 10;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  text-align: center;
  margin-top: ${theme.spacing.sm};
`;

const AmountDisplay = styled.div`
  text-align: center;
  margin-bottom: 0;
  padding: ${theme.spacing.md};
  background: ${theme.colors.doryBlue};
  border-radius: ${theme.borderRadius.md};
  
  .amount {
    font-size: 2rem;
    font-weight: ${theme.typography.weights.bold};
    color: ${theme.colors.primary};
  }
  
  .description {
    margin-top: ${theme.spacing.sm};
    color: white;
    font-size: ${theme.typography.sizes.small};
  }
`;

const GuaranteeBox = styled.div`
  text-align: center;
  margin: 0 auto ${theme.spacing.lg} auto;
  padding: ${theme.spacing.md};
  background: ${theme.colors.doryGreen};
  border-radius: ${theme.borderRadius.md};
  max-width: 500px;
  
  .guarantee-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: white;
    margin-bottom: ${theme.spacing.sm};
  }
  
  .guarantee-text {
    color: white;
    font-size: ${theme.typography.sizes.small};
    line-height: ${theme.typography.lineHeights.normal};
  }
`;

const ProductImage = styled.img`
  max-width: 312px;
  max-height: 312px;
  width: auto;
  height: auto;
  margin: 0 auto ${theme.spacing.lg} auto;
  display: block;
  object-fit: contain;
`;

const DiscountText = styled.div`
  text-align: center;
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.sm};
  font-style: italic;
`;

// Bar Chart Components (from MembershipSummaryScreen)
const WinRateContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: 0 auto 10px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WinRateTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-top: 50px;
  margin-bottom: 0;
  text-align: center;
  
  /* Scale smaller on mobile screens - only adjust font size */
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.h4};
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.bodyLarge};
  }
`;

const WinRateChart = styled.div`
  width: 100%;
  max-width: 80vw;
  height: 150px;
  position: relative;
  background: ${theme.colors.surface};
  padding: 0 ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md};
  box-sizing: border-box;
`;

const WinRateBar = styled.div`
  position: absolute;
  bottom: 0;
  width: ${props => props.$width}%;
  height: ${props => props.$height}%;
  background: ${props => {
    if (props.$isTargetRange) return theme.colors.doryLighterBlue; // Light blue for target range
    return `${theme.colors.doryGreen}80`; // Light green for others
  }};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  left: ${props => props.$left}%;
  
  &:hover {
    transform: scaleY(1.05);
    transform-origin: bottom;
  }
`;

const WinRateLabel = styled.div`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.textLight};
  font-weight: ${props => props.$isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const DateLabel = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 11px;
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  white-space: nowrap;
`;

const SVGLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const PaymentForm = ({ 
  amount, 
  productId, 
  productName, 
  productImage = null,
  ctaText = 'Pay Now',
  activeCampaignTagId = null,
  checkpointKey = null,
  checkpointType = 'payment', // New prop to specify checkpoint type
  isSimple = false,
  selectedWeek = null, // New prop to control visibility
  selectedWeekPrice = null, // New prop for the selected week's price
  isSubscription = false, // New prop to indicate subscription payment
  subscriptionPriceId = null, // Stripe price ID for subscription
  membershipPriceDisplay = null, // Price display text for membership subscriptions
  onSuccess, 
  onError, 
  userProgress = {} 
}) => {
  const { enableStripe } = useStripeContext();
  const stripe = useStripe();
  const elements = useElements();

  // Enable Stripe when PaymentForm mounts
  useEffect(() => {
    enableStripe();
  }, [enableStripe]);
  
  
  const [formData, setFormData] = useState({
    name: userProgress.userInfo?.name || '',
    email: userProgress.userInfo?.email || '',
    cvc: '',
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  // Calculate discount information
  const originalPrice = 342; // Original price before discount
  const currentPrice = amount ? Math.round(amount) : 0; // Regular payment uses amount as-is
  const discountPercentage = currentPrice > 0 ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;

  // Calculate deposit amount based on selected week's price
  const depositAmount = selectedWeekPrice ? Math.max(1.00, selectedWeekPrice * 0.25) : 0;

  // Calculate guarantee box content
  const optimalPaceData = userProgress.calculatedMetrics?.optimalPace;
  const membershipData = userProgress.membershipSelection;
  const targetPace = membershipData?.targetOptimalPace || optimalPaceData?.optimalHours || 13;
  const currentPace = Math.round(optimalPaceData?.optimalHours || 13);
  const pointsToImprove = targetPace - currentPace;
  
  // Calculate target date (today + 17 days * (targetPace - currentPace))
  const today = new Date();
  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + (17 * Math.max(pointsToImprove, 1)));
  
  // Format target date as "January 2nd 2026"
  const month = targetDate.toLocaleDateString('en-US', { month: 'long' });
  const day = targetDate.getDate();
  const year = targetDate.getFullYear();
  
  // Add ordinal suffix
  let ordinalSuffix;
  if (day >= 11 && day <= 13) {
    ordinalSuffix = 'th';
  } else {
    switch (day % 10) {
      case 1: ordinalSuffix = 'st'; break;
      case 2: ordinalSuffix = 'nd'; break;
      case 3: ordinalSuffix = 'rd'; break;
      default: ordinalSuffix = 'th'; break;
    }
  }
  
  const targetDateString = `${month} ${day}${ordinalSuffix} ${year}`;

  // Helper function to render the win rate visualization
  const renderWinRateVisualization = () => {
    const optimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    const membershipData = userProgress.membershipSelection;
    
    if (!optimalPaceData?.optimalHours || !membershipData) return null;

    const currentPace = Math.round(optimalPaceData.optimalHours);
    const targetPace = membershipData.targetOptimalPace || currentPace;
    
    // Show bars 13 to 18 when Optimal Pace is 18, otherwise show from current pace to target pace
    let startPace, endPace;
    if (currentPace === 18) {
      startPace = 13;
      endPace = 18;
    } else {
      startPace = currentPace;
      endPace = targetPace;
    }
    
    const totalBars = endPace - startPace + 1;
    const barWidth = 100 / totalBars;
    
    return (
      <WinRateContainer>
        <WinRateChart>
          {/* Render bars from current pace to target pace */}
          {Array.from({ length: totalBars }, (_, index) => {
            const pace = startPace + index;
            const winRate = Math.round((pace / 18) * 100);
            
            // Create more variance in bar heights - first bars smaller, later bars larger
            // Use a progressive scaling factor that makes early bars shorter and later bars taller
            const progressRatio = index / (totalBars - 1); // 0 to 1
            const varianceFactor = 0.3 + (0.7 * progressRatio); // 0.3 to 1.0
            const barHeight = winRate * varianceFactor;
            
            const barLeft = (index * barWidth);
            const isCurrentPace = pace === currentPace;
            const isTargetPace = pace === targetPace;
            
            // Show "Today" for first bar, then calculate dates for subsequent bars
            let dateString;
            if (index === 0) {
              dateString = "Today";
            } else {
              // Calculate date for this bar (today + 17 days * index)
              const today = new Date();
              const barDate = new Date(today);
              barDate.setDate(today.getDate() + (17 * index));
              
              // Format date as "Sept 22nd" with ordinal suffix
              const month = barDate.toLocaleDateString('en-US', { month: 'short' });
              const day = barDate.getDate();
              
              // Add ordinal suffix (st, nd, rd, th)
              let ordinalSuffix;
              if (day >= 11 && day <= 13) {
                ordinalSuffix = 'th';
              } else {
                switch (day % 10) {
                  case 1: ordinalSuffix = 'st'; break;
                  case 2: ordinalSuffix = 'nd'; break;
                  case 3: ordinalSuffix = 'rd'; break;
                  default: ordinalSuffix = 'th'; break;
                }
              }
              
              dateString = `${month} ${day}${ordinalSuffix}`;
            }
            
            return (
              <WinRateBar
                key={pace}
                $width={barWidth - 1} // Small gap between bars
                $height={barHeight}
                $left={barLeft}
                $isTargetRange={true} // All bars in this range are highlighted
              >
                {currentPace !== 18 && <DateLabel>{dateString}</DateLabel>}
                <WinRateLabel $isBold={isCurrentPace || isTargetPace}>{pace}</WinRateLabel>
              </WinRateBar>
            );
          })}
          
          {/* Connecting lines between each bar */}
          {totalBars > 1 && currentPace !== 18 && (() => {
            const lines = [];
            
            for (let i = 0; i < totalBars - 1; i++) {
              const currentBarIndex = i;
              const nextBarIndex = i + 1;
              
              // Calculate horizontal centers
              const currentBarCenter = (currentBarIndex * barWidth) + ((barWidth - 1) / 2);
              const nextBarCenter = (nextBarIndex * barWidth) + ((barWidth - 1) / 2);
              
              // Calculate vertical positions (middle of each bar's height)
              const currentPaceValue = startPace + i;
              const nextPaceValue = startPace + i + 1;
              const currentWinRate = Math.round((currentPaceValue / 18) * 100);
              const nextWinRate = Math.round((nextPaceValue / 18) * 100);
              
              // Apply the same variance factor to line calculations
              const currentProgressRatio = i / (totalBars - 1);
              const nextProgressRatio = (i + 1) / (totalBars - 1);
              const currentVarianceFactor = 0.3 + (0.7 * currentProgressRatio);
              const nextVarianceFactor = 0.3 + (0.7 * nextProgressRatio);
              
              const currentBarHeight = currentWinRate * currentVarianceFactor;
              const nextBarHeight = nextWinRate * nextVarianceFactor;
              
              // Position line endpoints at the middle of each bar's height
              const currentBarMiddle = 100 - currentBarHeight + (currentBarHeight * 0.5);
              const nextBarMiddle = 100 - nextBarHeight + (nextBarHeight * 0.5);
              
              lines.push(
                <line
                  key={`line-${i}`}
                  x1={`${currentBarCenter}%`}
                  y1={`${currentBarMiddle}%`}
                  x2={`${nextBarCenter}%`}
                  y2={`${nextBarMiddle}%`}
                  stroke={theme.colors.doryDarkerBlue}
                  strokeWidth="4"
                />
              );
            }
            
            return (
              <SVGLine>
                {lines}
              </SVGLine>
            );
          })()}
        </WinRateChart>
        <WinRateTitle>Your Optimal Pace Journey Starts Now</WinRateTitle>
      </WinRateContainer>
    );
  };

  // Pre-populate form with existing user data
  useEffect(() => {
    if (userProgress.userInfo) {
      setFormData(prev => ({
        ...prev,
        name: userProgress.userInfo.name || prev.name,
        email: userProgress.userInfo.email || prev.email,
      }));
    }
  }, [userProgress.userInfo]);

  // Handle mobile keyboard events
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Scroll the focused element into view
        setTimeout(() => {
          e.target.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 300);
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Ensure we can scroll back to top after keyboard closes
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      }
    };

    // Add event listeners
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Cleanup
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const savePaymentFormUserInfo = async () => {
    if (!userProgress?.shortcode) {
      return;
    }

    const currentUserInfo = userProgress.userInfo || {};
    const updatedUserInfo = { ...currentUserInfo };

    if (formData.email) {
      updatedUserInfo.email = formData.email.trim();
    }
    if (formData.name) {
      updatedUserInfo.name = formData.name.trim();
    }

    // Nothing new to write
    if (
      updatedUserInfo.email === currentUserInfo.email &&
      updatedUserInfo.name === currentUserInfo.name
    ) {
      return;
    }

    await updateUserProgress(userProgress.shortcode, {
      userInfo: updatedUserInfo,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    // Validate amount (only for non-subscription payments)
    if (!isSubscription && (!amount || amount <= 0)) {
      setError('Invalid amount. Please check the product configuration.');
      return;
    }

    // Validate subscription price ID for subscription payments
    if (isSubscription && !subscriptionPriceId) {
      setError('Invalid subscription configuration. Please check the membership options.');
      return;
    }

    // Validate form data
    const validation = validatePaymentForm(formData);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }
    
    if (!cardComplete) {
      setError('Please complete your card information.');
      return;
    }

    setIsProcessing(true);
    setError('');

    // Persist name/email to userInfo before charging so declines still capture lead info
    try {
      await savePaymentFormUserInfo();
    } catch (userInfoError) {
      console.error('Failed to save payment form userInfo before charge:', userInfoError);
      // Continue with payment attempt — do not block charge on this write
    }

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: formData.name,
          email: formData.email,
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        return;
      }

      // Process the payment or subscription
      const result = isSubscription 
        ? await processSubscriptionWithStripe(
            paymentMethod.id,
            subscriptionPriceId,
            formData.email
          )
        : await processPaymentWithStripe(
            paymentMethod.id,
            amount,
            productId,
            formData.email
          );

      if (result.success) {
        setSuccess(true);
        setError('');
        
        // Re-save userInfo (idempotent) and integrate with ActiveCampaign
        try {
          if (userProgress.shortcode) {
            try {
              await savePaymentFormUserInfo();
            } catch (userInfoError) {
              console.error('Failed to re-save payment form userInfo after success:', userInfoError);
            }
            
            // Integrate with ActiveCampaign if we have an email (same logic as email_capture)
            if (formData.email) {
              try {
                const contactData = {
                  email: formData.email,
                  firstName: formData.name ? formData.name.split(' ')[0] : '',
                  lastName: formData.name ? formData.name.split(' ').slice(-1)[0] : '',
                  phone: '', // Payment form doesn't collect phone
                  shortCode: userProgress.shortcode,
                  tagName: activeCampaignTagId // Use the tag from payment screen configuration
                };
                
                const contactResult = await createOrUpdateContact(contactData);
                
                // Store ActiveCampaign contact ID for future updates
                if (contactResult.contactId) {
                  await updateUserProgress(userProgress.shortcode, {
                    activeCampaignId: contactResult.contactId
                  });
                  
                  // Add tag if specified
                  if (activeCampaignTagId) {
                    try {
                      await addContactTag(contactResult.contactId, null, activeCampaignTagId);
                    } catch (tagError) {
                      // Don't fail the entire payment process if tag addition fails
                    }
                  }
                  
                  // Send Optimal Pace and Mental Resilience values to ActiveCampaign
                  try {
                    const customFields = {
                      shortCode: userProgress.shortcode
                    };
                    
                    // Add Mental Resilience if available
                    if (userProgress.calculatedMetrics?.mentalResilience?.score) {
                      customFields.mentalResilience = userProgress.calculatedMetrics.mentalResilience.score;
                    }
                    
                    // Add Optimal Pace if available
                    if (userProgress.calculatedMetrics?.optimalPace?.optimalHoursFormatted) {
                      customFields.optimalPace = userProgress.calculatedMetrics.optimalPace.optimalHoursFormatted;
                    }
                    
                    if (Object.keys(customFields).length > 1) { // More than just shortCode
                      await updateContactCustomFields(contactResult.contactId, customFields);
                    }
                  } catch (customFieldsError) {
                    // Continue without custom fields update
                  }
                }
                
              } catch (activeCampaignError) {
                // Continue without ActiveCampaign integration
                
                // Store the error for retry later (same as email_capture)
                const pendingSyncs = userProgress.pendingActiveCampaignSync || [];
                pendingSyncs.push({
                  type: 'payment',
                  data: {
                    contactId: userProgress.activeCampaignId,
                    tagId: activeCampaignTagId // Use the actual tag ID
                  },
                  error: activeCampaignError.message,
                  timestamp: Date.now()
                });
                
                await updateUserProgress(userProgress.shortcode, {
                  pendingActiveCampaignSync: pendingSyncs
                });
              }
            }
          }
        } catch (error) {
          // Don't fail the payment if post-success side effects fail
        }
        
        // Call success callback with payment or subscription details
        if (onSuccess) {
          if (isSubscription) {
            onSuccess({
              subscriptionId: result.subscriptionId,
              customerId: result.customerId,
              paymentIntent: result.paymentIntent,
              transactionId: result.transactionId,
              customerEmail: formData.email,
              customerName: formData.name,
              priceId: result.priceId,
              paymentMethodId: paymentMethod.id,
              screenKey: 'payment_membership' // This will be overridden by the parent component if needed
            });
          } else {
            onSuccess({
              paymentIntent: result.paymentIntent,
              transactionId: result.transactionId,
              customerEmail: formData.email,
              customerName: formData.name,
              amount: amount,
              productId: productId,
              paymentMethodId: paymentMethod.id,
              screenKey: 'payment' // This will be overridden by the parent component if needed
            });
          }
        }
      } else {
        const errorMessage = result.error || 'Payment failed. Please try again.';
        
        // Always display error in PaymentForm (right above CTA button)
        setError(errorMessage);
        
        // Also call parent callback if provided
        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      
      // Always display error in PaymentForm (right above CTA button)
      setError(errorMessage);
      
      // Also call parent callback if provided
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Process payment with Stripe using Firebase function
  const processPaymentWithStripe = async (paymentMethodId, amount, productId, customerEmail) => {
    try {
      // Call the Firebase function to create payment intent
      // Use test function on localhost, live function in production
      
      // Determine mode based on hostname
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      const functionName = isDevelopment ? 'createPaymentIntentTest' : 'createPaymentIntent';
      
      const createPaymentIntent = httpsCallable(functions, functionName);
      
      
      
      // Convert amount to cents for Stripe (amount should be in dollars, convert to cents)
      // Ensure minimum amount of $0.50 (50 cents) for Stripe
      const amountInCents = Math.max(Math.round(amount * 100), 50);
      
      
      const result = await createPaymentIntent({
        amount: amountInCents, // Send amount in cents to Firebase function
        productId: productId,
        customerEmail: customerEmail,
        customerName: formData.name
      });


      if (result.data.success) {
        
        // Confirm the payment with Stripe
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          result.data.clientSecret,
          {
            payment_method: paymentMethodId,
          }
        );

        if (error) {
          throw new Error(error.message);
        }


        return {
          success: true,
          paymentIntent: paymentIntent,
          transactionId: paymentIntent.id,
          customerEmail: customerEmail,
          customerName: formData.name,
          amount: amount,
          productId: productId,
          paymentMethodId: paymentMethodId
        };
      } else {
        throw new Error('Failed to create payment intent');
      }
    } catch (error) {
      // Firebase callable function errors have a specific structure
      // Extract the actual error message from Firebase error objects
      let errorMessage = 'An error occurred while processing your payment. Please try again.';
      
      // Log the full error for debugging
      console.error('Payment processing error:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        stack: error?.stack
      });
      
      if (error) {
        // Firebase errors have a 'code' property that indicates the error type
        // Common codes: 'internal', 'invalid-argument', 'permission-denied', etc.
        const errorCode = error.code;
        
        // If the error message is just "internal" or the code is "internal",
        // try to extract more meaningful information
        if (errorCode === 'internal' || 
            (error.message && (error.message === 'internal' || error.message.toLowerCase().includes('internal')))) {
          
          // Try to get more details from the error
          if (error.details) {
            const details = error.details;
            // If details is a string, use it directly
            if (typeof details === 'string') {
              errorMessage = details;
            } 
            // If details is an object, try to extract a message
            else if (typeof details === 'object') {
              errorMessage = details.message || details.error || JSON.stringify(details);
            }
          } 
          // If we have a code but no useful message, provide a user-friendly message
          else if (errorCode === 'internal') {
            errorMessage = 'An internal error occurred while processing your payment. Please check your payment information and try again. If the problem persists, please contact support.';
          }
          // Use the error message if it's not just "internal"
          else if (error.message && error.message !== 'internal') {
            errorMessage = error.message;
          }
        } 
        // For other error codes, use the message if available
        else if (error.message) {
          errorMessage = error.message;
        } 
        // Fallback to code-based message
        else if (errorCode) {
          errorMessage = `Payment error (${errorCode}). Please check your payment information and try again.`;
        }
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  // Process subscription with Stripe using Firebase function
  const processSubscriptionWithStripe = async (paymentMethodId, priceId, customerEmail) => {
    try {
      // Call the Firebase function to create subscription
      // Use test function on localhost, live function in production
      
      // Determine mode based on hostname
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      const functionName = isDevelopment ? 'createSubscriptionTest' : 'createSubscription';
      
      const createSubscription = httpsCallable(functions, functionName);
      
      const result = await createSubscription({
        priceId: priceId,
        customerEmail: customerEmail,
        customerName: formData.name,
        paymentMethodId: paymentMethodId
      });


      if (result && result.data && result.data.success) {
        // Check if payment is required
        if (result.data.requiresPayment === false || !result.data.clientSecret) {
          // Subscription is already active, no payment needed
          
          return {
            success: true,
            subscriptionId: result.data.subscriptionId,
            customerId: result.data.customerId,
            paymentIntent: null, // No payment intent needed
            transactionId: result.data.subscriptionId, // Use subscription ID as transaction ID
            customerEmail: customerEmail,
            customerName: formData.name,
            priceId: priceId,
            paymentMethodId: paymentMethodId,
            requiresPayment: false
          };
        }

        // Verify we have a client secret for payment
        if (!result.data.clientSecret) {
          throw new Error('Subscription created but no payment confirmation available. Please contact support.');
        }


        // Confirm the payment with Stripe
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          result.data.clientSecret,
          {
            payment_method: paymentMethodId,
          }
        );

        if (error) {
          console.error('Stripe payment confirmation error:', error);
          throw new Error(error.message || 'Payment confirmation failed. Please try again.');
        }

        if (!paymentIntent) {
          throw new Error('Payment confirmation did not return a payment intent. Please try again.');
        }


        return {
          success: true,
          subscriptionId: result.data.subscriptionId,
          customerId: result.data.customerId,
          paymentIntent: paymentIntent,
          transactionId: paymentIntent.id,
          customerEmail: customerEmail,
          customerName: formData.name,
          priceId: priceId,
          paymentMethodId: paymentMethodId
        };
      } else {
        // If result.data doesn't exist or success is false, extract error info
        const errorInfo = result?.data?.error || result?.data?.message || 'Unknown error';
        throw new Error(`Failed to create subscription: ${errorInfo}`);
      }
    } catch (error) {
      // Firebase callable function errors have a specific structure
      // Extract the actual error message from Firebase error objects
      let errorMessage = 'An error occurred while processing your subscription. Please try again.';
      
      if (error) {
        const errorCode = error.code;
        
        // HttpsError from Firebase Functions v2 has the message in error.message
        // Check error.message first - this is where HttpsError stores the actual message
        if (error.message) {
          const msg = error.message.trim();
          
          // If message contains "Failed to create subscription:" or "Failed to create test subscription:", extract the actual error
          if (msg.includes('Failed to create subscription:') || msg.includes('Failed to create test subscription:')) {
            const parts = msg.split(':');
            if (parts.length > 1) {
              const actualError = parts.slice(1).join(':').trim();
              if (actualError && actualError.length > 0) {
                errorMessage = actualError;
              } else {
                errorMessage = msg;
              }
            } else {
              errorMessage = msg;
            }
          }
          // Check for Stripe-specific error messages or any meaningful error
          else if (msg && msg.length > 0 && msg !== 'internal' && msg !== 'functions/internal') {
            errorMessage = msg;
          }
        }
        
        // Handle Firebase Functions internal errors
        if ((errorCode === 'functions/internal' || errorCode === 'internal') && errorMessage === 'An error occurred while processing your subscription. Please try again.') {
          // Try error.details if we still have default message
          if (error.details) {
            const details = error.details;
            if (typeof details === 'string' && details.trim().length > 0) {
              errorMessage = details.trim();
            } else if (typeof details === 'object') {
              const detailMsg = details.message || details.error || details.msg || details.originalError;
              if (detailMsg && typeof detailMsg === 'string' && detailMsg.trim().length > 0) {
                errorMessage = detailMsg.trim();
              }
            }
          }
          
          // Final fallback for internal errors
          if (errorMessage === 'An error occurred while processing your subscription. Please try again.') {
            errorMessage = 'An internal error occurred while processing your subscription. Please check your payment information and try again. If the problem persists, please contact support.';
          }
        }
        // Handle other error codes
        else if (errorCode) {
          if (error.message) {
            const msg = error.message.trim();
            // Extract actual error from wrapped messages
            if (msg.includes('Failed to create subscription:') || msg.includes('Failed to create test subscription:')) {
              const parts = msg.split(':');
              if (parts.length > 1) {
                const actualError = parts.slice(1).join(':').trim();
                if (actualError && actualError.length > 0) {
                  errorMessage = actualError;
                } else {
                  errorMessage = msg;
                }
              } else {
                errorMessage = msg;
              }
            } else if (msg && msg.length > 0) {
              errorMessage = msg;
            } else {
              errorMessage = `Subscription error (${errorCode}). Please check your payment information and try again.`;
            }
          } else {
            errorMessage = `Subscription error (${errorCode}). Please check your payment information and try again.`;
          }
        }
        // No error code, just use the message
        else if (error.message) {
          const msg = error.message.trim();
          if (msg.includes('Failed to create subscription:') || msg.includes('Failed to create test subscription:')) {
            const parts = msg.split(':');
            if (parts.length > 1) {
              const actualError = parts.slice(1).join(':').trim();
              if (actualError && actualError.length > 0) {
                errorMessage = actualError;
              } else {
                errorMessage = msg;
              }
            } else {
              errorMessage = msg;
            }
          } else if (msg && msg.length > 0) {
            errorMessage = msg;
          }
        }
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  if (success) {
    // Don't render anything - the onSuccess callback should handle advancing to the next step
    return null;
  }

  // Determine checkpoint type
  const isMarketingLandingPage = checkpointType === 'marketing_landing_page';
  const isPaymentMembership = checkpointType === 'payment_membership';
  
  // For marketing landing page, only show payment form when a week is actually selected
  // For regular payment, always show payment form
  // For payment membership, always show payment form (membership selection is handled in parent)
  const shouldShowPaymentForm = !isSimple && (
    !isMarketingLandingPage || (isMarketingLandingPage && selectedWeek)
  ) || isPaymentMembership;

  return (
    <FormContainer>
      {/* Bar Chart - only show for regular payment, not payment_simple, marketing landing page, or payment_membership */}
      {!isSimple && !isMarketingLandingPage && !isPaymentMembership && renderWinRateVisualization()}
      
      {/* Product Details Box - only show for regular payment, not payment_simple, marketing landing page, or payment_membership */}
      {!isSimple && !isMarketingLandingPage && !isPaymentMembership && (
        <div style={{
          textAlign: 'center',
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.lg,
          background: theme.colors.doryBlue,
          borderRadius: theme.borderRadius.md,
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: theme.typography.weights.bold,
            marginBottom: theme.spacing.sm,
            color: 'white'
          }}>
            {productName}
          </h3>
          <p style={{
            fontSize: theme.typography.sizes.body,
            lineHeight: theme.typography.lineHeights.normal,
            margin: 0,
            color: 'white'
          }}>
            This next step changes everything. Join 1,646 members.
          </p>
        </div>
      )}

      {/* Price Display - show for regular payment and marketing landing page when week is selected, but not for payment_membership */}
      {shouldShowPaymentForm && !isPaymentMembership && (
        <div style={{
          textAlign: 'center',
          marginBottom: theme.spacing.lg
        }}>
          {/* Product Image - only show for regular payment, right above price */}
          {!isSimple && !isMarketingLandingPage && productImage && (
            <ProductImage src={productImage} alt={productName} />
          )}
          
          <div style={{
            fontSize: '3rem',
            fontWeight: '400',
            color: theme.colors.primary,
            marginBottom: '20px'
          }}>
            ${isMarketingLandingPage ? depositAmount.toFixed(0) : currentPrice.toFixed(0)}
          </div>
          <div style={{
            fontSize: '1rem',
            fontWeight: '400',
            color: theme.colors.textLight
          }}>
            {isMarketingLandingPage ? (
              <>
                25% deposit due now holds your seat for the week. Fully refundable up until one month before the week starts.<br></br><br></br>
                Plus your share of food and activity fees billed separately and at cost after the event. You are responsible for your own travel and lodging.
              </>
            ) : (
              <>
                  <DiscountText>
                     You pay $1 for your first month of membership. At your next monthly renewal, you'll only pay more if you've increased your Optimal Pace above {currentPace}.</DiscountText>
              </>
            )}
          </div>
        </div>
      )}

      {/* Guarantee Box - only show for regular payment, not payment_simple, marketing landing page, or payment_membership */}
      {!isSimple && !isMarketingLandingPage && !isPaymentMembership && (
        <GuaranteeBox>
          <div className="guarantee-title">We Guarantee Results</div>
          <div className="guarantee-text">
             We're proud to support you with everything you need to run the entire Optimal Pace Protocol™ in your life. You only pay $1 for membership until your Optimal Pace grows.
          </div>
        </GuaranteeBox>
      )}

      {/* Payment Form - show for regular payment and payment_simple, or marketing landing page when week is selected */}
      {shouldShowPaymentForm && (
        <form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="Full Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          required
          style={{ width: '100%', marginBottom: theme.spacing.sm }}
        />
        
        <Input
          type="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          required
          style={{ width: '100%' }}
        />
        
        {/* Membership Price Summary - only for payment_membership */}
        {isPaymentMembership && membershipPriceDisplay && (() => {
          // Extract membership type from productName (e.g., "Protocol Membership" -> "Protocol")
          const membershipType = productName ? productName.replace(/\s+Membership$/, '') : '';
          
          // Parse the price display to extract amount and period
          // Format: "$19 per month" or "$4,788 per year"
          const priceMatch = membershipPriceDisplay.match(/\$([\d,]+)\s+per\s+(month|year)/);
          if (priceMatch) {
            const amount = priceMatch[1];
            const period = priceMatch[2]; // "month" or "year"
            const periodText = period === 'month' ? 'month' : 'year';
            
            return (
              <div style={{
                marginTop: theme.spacing.lg,
                marginBottom: theme.spacing.md,
                padding: theme.spacing.md,
                background: theme.colors.disabled || '#E0E0E0',
                borderRadius: theme.borderRadius.md,
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: theme.typography.sizes.body,
                  color: theme.colors.text || '#121616',
                  lineHeight: theme.typography.lineHeights.normal || '1.5'
                }}>
                  You'll be charged <strong>${amount}</strong> for your first {periodText} of {membershipType} membership. Cancel your membership anytime.
                </div>
              </div>
            );
          }
          // Fallback if parsing fails - try alternative format "$19/month" or "$4,788/year"
          const fallbackMatch = membershipPriceDisplay.match(/\$([\d,]+)\/(month|year)/);
          if (fallbackMatch) {
            const amount = fallbackMatch[1];
            const period = fallbackMatch[2];
            const periodText = period === 'month' ? 'month' : 'year';
            return (
              <div style={{
                marginTop: theme.spacing.lg,
                marginBottom: theme.spacing.md,
                padding: theme.spacing.md,
                background: theme.colors.disabled || '#E0E0E0',
                borderRadius: theme.borderRadius.md,
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: theme.typography.sizes.body,
                  color: theme.colors.text || '#121616',
                  lineHeight: theme.typography.lineHeights.normal || '1.5'
                }}>
                  You'll be charged <strong>${amount}</strong> for your first {periodText} of {membershipType} membership. Cancel your membership anytime.
                </div>
              </div>
            );
          }
          // Final fallback - just extract amount
          const amountMatch = membershipPriceDisplay.match(/\$([\d,]+)/);
          if (amountMatch) {
            const amount = amountMatch[1];
            return (
              <div style={{
                marginTop: theme.spacing.lg,
                marginBottom: theme.spacing.md,
                padding: theme.spacing.md,
                background: theme.colors.disabled || '#E0E0E0',
                borderRadius: theme.borderRadius.md,
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: theme.typography.sizes.body,
                  color: theme.colors.text || '#121616',
                  lineHeight: theme.typography.lineHeights.normal || '1.5'
                }}>
                  You'll be charged <strong>${amount}</strong> for your first month of {membershipType} membership. Cancel your membership anytime.
                </div>
              </div>
            );
          }
          // Ultimate fallback
          return (
            <div style={{
              marginTop: theme.spacing.lg,
              marginBottom: theme.spacing.md,
              padding: theme.spacing.md,
              background: theme.colors.disabled || '#E0E0E0',
              borderRadius: theme.borderRadius.md,
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: theme.typography.sizes.body,
                color: theme.colors.text || '#121616',
                lineHeight: theme.typography.lineHeights.normal || '1.5'
              }}>
                You'll be charged {membershipPriceDisplay} for your first month of {membershipType} membership. Cancel your membership anytime.
              </div>
            </div>
          );
        })()}
        
        <div style={{ marginTop: '8px', marginBottom: theme.spacing.md }}>
          {stripe ? (
            <StripeCardElement>
              <CardElement
                options={{
                  style: STRIPE_CONFIG.elementStyles,
                  hidePostalCode: false,
                }}
                onChange={handleCardChange}
              />
            </StripeCardElement>
          ) : (
            <div style={{ 
              fontSize: '12px', 
              color: theme.colors.warning, 
              marginTop: '4px', 
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              Stripe not loaded - please refresh the page
            </div>
          )}
        </div>
        
        {/* Display payment failure messages below the credit card fields */}
        {error && error.trim() !== '' && (
          <ErrorMessage style={{ 
            marginTop: theme.spacing.lg,
            marginBottom: theme.spacing.sm 
          }}>
            {error}
            {/* Show raw error details in development mode */}
          </ErrorMessage>
        )}
        
        <SubmitButton 
          type="submit" 
          disabled={
            isProcessing || 
            !formData.name || 
            !formData.email ||
            (stripe ? !cardComplete : false) ||
            (isPaymentMembership && !subscriptionPriceId)
          }
        >
          {isProcessing ? 'Processing...' : (
            isMarketingLandingPage ? `Lock In My Spot in Week ${selectedWeek}` : (ctaText || 'Pay Now')
          )}
        </SubmitButton>
        </form>
      )}
    </FormContainer>
  );
};

export default PaymentForm;
